/**
 * GeoPriorityChecker
 *
 * Rules (a self-check, not a forecast; no numbers are invented):
 * - Four questions: practice area, how quickly clients usually hire, current SEO foundation,
 *   and number of offices. A result appears once all four are answered.
 * - Foundation "little or none": fix technical and local SEO first, then add GEO.
 *   This rule takes priority because AI answers draw on pages that already rank.
 * - Otherwise, hiring "within hours": prioritise Google SEO, Google Business Profile and
 *   paid search, with GEO as a second layer.
 * - Otherwise (hiring "within days" or "weeks or longer"): GEO deserves a place in the plan now.
 * - Two or more offices adds a note that every office needs its own consistent entity data.
 * - Every result links to the matching practice-area page plus the relevant service page.
 * - No tracking, storage, cookies or network calls.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";

interface Option {
  value: string;
  label: string;
}

const PRACTICE: (Option & { href: string })[] = [
  { value: "dui", label: "DUI / DWI", href: "/practice-areas/dui-dwi-lawyer-seo" },
  { value: "criminal", label: "Criminal defense", href: "/practice-areas/criminal-defense-lawyer-seo" },
  { value: "pi", label: "Personal injury", href: "/practice-areas/personal-injury-lawyer-seo" },
  { value: "family", label: "Family law", href: "/practice-areas/family-law-lawyer-seo" },
  { value: "estate", label: "Estate planning", href: "/practice-areas/estate-planning-lawyer-seo" },
  { value: "immigration", label: "Immigration", href: "/practice-areas/immigration-lawyer-seo" },
  { value: "business", label: "Business law", href: "/practice-areas/corporate-business-lawyer-seo" },
  { value: "other", label: "Another practice area", href: "/practice-areas" },
];

const SPEED: Option[] = [
  { value: "hours", label: "Within hours" },
  { value: "days", label: "Within days" },
  { value: "weeks", label: "Weeks or longer" },
];

const FOUNDATION: Option[] = [
  { value: "strong", label: "Page one for our main terms" },
  { value: "some", label: "Some rankings" },
  { value: "none", label: "Little or none" },
];

const OFFICES: Option[] = [
  { value: "one", label: "One office" },
  { value: "few", label: "2 to 4 offices" },
  { value: "many", label: "5 or more" },
];

interface Answers {
  practice: string | null;
  speed: string | null;
  foundation: string | null;
  offices: string | null;
}

function ChoiceGroup({
  id,
  question,
  options,
  value,
  onChange,
}: {
  id: string;
  question: string;
  options: Option[];
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  return (
    <div>
      <p id={id} className="text-sm font-medium">
        {question}
      </p>
      <div role="group" aria-labelledby={id} className="mt-2 flex flex-wrap gap-2">
        {options.map((o) => {
          const selected = value === o.value;
          return (
            <Button variant={selected ? "default" : "outline"}
              key={o.value}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(selected ? null : o.value)}
              className={`h-auto whitespace-normal rounded-md border px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                selected
                  ? "border-border bg-primary text-primary-foreground"
                  : "border-input bg-card text-card-foreground"
              }`}
            >
              {o.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

const linkClass =
  "font-medium underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export default function GeoPriorityChecker() {
  const [a, setA] = useState<Answers>({
    practice: null,
    speed: null,
    foundation: null,
    offices: null,
  });

  const set = (key: keyof Answers) => (v: string | null) => setA((prev) => ({ ...prev, [key]: v }));
  const complete = a.practice && a.speed && a.foundation && a.offices;
  const practice = PRACTICE.find((p) => p.value === a.practice);

  let heading = "";
  let body = "";
  let service = { href: "", text: "" };

  if (complete) {
    if (a.foundation === "none") {
      heading = "Fix your foundations first, then add GEO";
      body =
        "AI answers draw on pages that already rank and that search engines understand. With little visibility today, technical fixes and local SEO will do more for you right now than GEO work on its own.";
      service = { href: "/services/technical-seo-for-law-firms", text: "Start with technical SEO for law firms" };
    } else if (a.speed === "hours") {
      heading = "Lead with Google SEO, your Business Profile and paid search";
      body =
        "Your clients search and call within hours, often from a phone. Map Pack visibility and paid search catch that moment best. Keep GEO as a second layer so AI tools describe your firm accurately when clients double-check you.";
      service = { href: "/services/local-seo-for-law-firms", text: "See local SEO for law firms" };
    } else {
      heading = "GEO belongs in your plan now";
      body =
        "Your clients research before they hire, which is when they ask AI tools to compare firms. With an SEO base already in place, entity data, answer-first practice pages and outside mentions can start earning citations.";
      service = { href: "/services/ai-seo-geo", text: "See AI SEO and GEO services for law firms" };
    }
  }

  return (
    <section className="not-prose my-8 rounded-lg border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">How much should your firm prioritise GEO?</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Answer four quick questions for a starting point based on how your clients hire.
      </p>

      <div className="mt-5 space-y-5">
        <ChoiceGroup
          id="geo-pc-practice"
          question="1. What is your main practice area?"
          options={PRACTICE}
          value={a.practice}
          onChange={set("practice")}
        />
        <ChoiceGroup
          id="geo-pc-speed"
          question="2. How quickly do most clients hire after their first search?"
          options={SPEED}
          value={a.speed}
          onChange={set("speed")}
        />
        <ChoiceGroup
          id="geo-pc-foundation"
          question="3. How visible is your firm in Google today?"
          options={FOUNDATION}
          value={a.foundation}
          onChange={set("foundation")}
        />
        <ChoiceGroup
          id="geo-pc-offices"
          question="4. How many offices do you have?"
          options={OFFICES}
          value={a.offices}
          onChange={set("offices")}
        />
      </div>

      <div aria-live="polite" className="mt-6 rounded-lg bg-muted p-4 text-sm">
        {!complete ? (
          <p className="text-muted-foreground">Answer all four questions to see your result.</p>
        ) : (
          <>
            <p className="text-base font-semibold">{heading}</p>
            <p className="mt-2">{body}</p>
            {a.offices !== "one" && (
              <p className="mt-2">
                With more than one office, each location needs its own consistent name, address and
                attorney data, or AI tools may merge or confuse your offices.
              </p>
            )}
            <p className="mt-3">
              <Link to={service.href} className={linkClass}>
                {service.text}
              </Link>
              {practice && (
                <>
                  {" "}
                  or read how we approach{" "}
                  <Link to={practice.href} className={linkClass}>
                    {practice.value === "other" ? "each practice area" : `${practice.label} SEO`}
                  </Link>
                  .
                </>
              )}
            </p>
          </>
        )}
      </div>
    </section>
  );
}
